/** @format */

import { useCallback, useEffect } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  add_To_card,
  add_To_wishlist,
  messageClear,
} from "../../store/reducers/cardReducers";
import { add_friend, send_message } from "../../store/reducers/chatReducer";
import ProductCard from "./ProductCard";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const computeFinalPrice = (price, discount) =>
  discount ? Math.floor((parseFloat(price) * (100 - discount)) / 100) : price;

const buildProductInfo = (product) => ({
  image: product.images[0]?.url ?? product.images[0],
  title: product.name,
  price: computeFinalPrice(product.price, product.discount),
  stock: product.stock,
  productId: product._id,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FutureProducts = ({ products = [] }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { customerInfo } = useSelector((state) => state.auth);
  const { successMessage, messageError } = useSelector((state) => state.card);

  // -- Side effects ----------------------------------------------------------

  useEffect(() => {
    if (messageError) {
      dispatch(messageClear());
    }

    if (successMessage) {
      dispatch(messageClear());
      const timer = setTimeout(() => navigate("/"), 500);
      return () => clearTimeout(timer);
    }
  }, [messageError, successMessage, dispatch, navigate]);

  // -- Handlers --------------------------------------------------------------

  const handleAddToCart = useCallback(
    (productId) => {
      if (!customerInfo) return navigate("/login");

      dispatch(
        add_To_card({
          customerId: customerInfo.id,
          productId,
          quantity: 1,
        }),
      );
    },
    [customerInfo, dispatch, navigate],
  );

  const handleAddToWishlist = useCallback(
    (product) => {
      if (!customerInfo) return;

      dispatch(
        add_To_wishlist({
          customerId: customerInfo.id,
          productId: product._id,
          name: product.name,
          price: product.price,
          image: product.images[0],
          discount: product.discount,
          rating: product.rating,
          slug: product.slug,
        }),
      );
    },
    [customerInfo, dispatch],
  );

  const handleSendProductMessage = useCallback(
    async (product) => {
      if (!customerInfo) return navigate("/login");

      const sellerId = product.sellerId._id;

      const payload = {
        senderName: customerInfo.name,
        customerId: customerInfo.id,
        sellerId,
        messageType: "product",
        message: "",
        productInfo: buildProductInfo(product),
      };

      await dispatch(add_friend({ sellerId, customerId: customerInfo.id }));

      dispatch({ type: "chat/updateFdMessages", payload });

      try {
        await dispatch(send_message(payload)).unwrap();
        toast.success("Message envoyé au vendeur !");
        navigate(`/dashboard/my_chats/${sellerId}`);
      } catch (err) {
        toast.error("Erreur lors de l'envoi du message");
        console.error("[FutureProducts] send_message error:", err);
      }
    },
    [customerInfo, dispatch, navigate],
  );

  // -- Render ----------------------------------------------------------------

  return (
    <section className='w-full max-w-[1600px] mx-auto px-2 sm:px-3 py-6'>
      <header className='text-center mb-4'>
        <h2 className='text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight'>
          {t("Products Features")}
        </h2>
        <div className='mt-3 mx-auto w-16 h-1 rounded-full bg-gradient-to-r from-blue-600 to-indigo-500' />
      </header>

      <div className='w-full max-w-[1600px] mx-auto grid grid-cols-6 md-lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4 justify-center'>
        {products.map((product, i) => (
          <ProductCard
            key={product._id ?? i}
            product={product}
            customerInfo={customerInfo}
            onAddCard={handleAddToCart}
            onAddWishlist={handleAddToWishlist}
            onSendMessage={handleSendProductMessage}
          />
        ))}
      </div>
    </section>
  );
};

export default FutureProducts;
